import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import { getAvisosPago, revisarAvisoPago, deleteAvisoPago } from "../services/portalService";
import { TableToolbar, FilterField, FilterSelect, FilterInput } from "../../finance/shared/TableToolbar";
import styles from "../../finance/invoices/styles/invoice.module.css";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const fn = () => setBp(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

const PAGE_SIZE = 10;

const ESTADOS = {
  PENDIENTE: { bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.3)",  text: "#f59e0b",  icon: "fa-clock",        label: "Pendiente"  },
  VALIDO:    { bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.3)",  text: "#10b981",  icon: "fa-circle-check", label: "Válido"     },
  NO_VALIDO: { bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)", text: "#f87171",  icon: "fa-circle-xmark", label: "No válido"  },
};

function EstadoBadge({ estado }) {
  const c = ESTADOS[estado] || { bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.3)", text: "#9ca3af", icon: "fa-circle", label: estado };
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: 20, padding: "4px 10px", fontSize: 11, fontWeight: 700,
      display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
    }}>
      <i className={`fa-solid ${c.icon}`} style={{ fontSize: 9 }} />
      {c.label}
    </span>
  );
}

function StatCard({ icon, label, value, color = "#f97316" }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 12, padding: "16px 20px", display: "flex",
      alignItems: "center", gap: 14, flex: "1 1 130px", minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, fontSize: 16,
      }}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 3, fontWeight: 500, whiteSpace: "nowrap" }}>{label}</div>
      </div>
    </div>
  );
}

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btn = (disabled, onClick, children) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
      background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
      cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
    }}>
      {children}
    </button>
  );
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 16px", borderTop: "1px solid var(--border-card)",
    }}>
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} de {total}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {btn(page === 1, () => onChange(page - 1), <i className="fa-solid fa-chevron-left" />)}
        <span style={{ padding: "5px 12px", borderRadius: 8, fontSize: 13, background: "#f97316", color: "#fff", fontWeight: 700 }}>
          {page} / {pages}
        </span>
        {btn(page === pages, () => onChange(page + 1), <i className="fa-solid fa-chevron-right" />)}
      </div>
    </div>
  );
}

export default function AdminAvisosPago() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";

  const [avisos,   setAvisos]   = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filtro,   setFiltro]   = useState("PENDIENTE");
  const [saving,   setSaving]   = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [filterFilial, setFilterFilial] = useState("");
  const [filterDesde,  setFilterDesde]  = useState("");
  const [filterHasta,  setFilterHasta]  = useState("");
  const [page,         setPage]         = useState(1);
  const [rechazo,    setRechazo]    = useState(null); // aviso que se está rechazando
  const [motivo,     setMotivo]     = useState("");
  const [motivoErr,  setMotivoErr]  = useState("");

  function load() {
    getAvisosPago().then(d => setAvisos(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/").then(r => r.json()).then(d => setFiliales(d.results || d)).catch(() => {});
  }, []);

  // Si un residente manda un aviso de pago nuevo mientras estamos acá, se
  // actualiza la lista sola en vez de esperar a que alguien la recargue a mano.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "PAGO") load();
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, []);

  async function handleRevisar(id, estado, notasAdmin = "") {
    setSaving(id);
    try {
      await revisarAvisoPago(id, estado, notasAdmin);
      showToast(estado === "VALIDO" ? t("portal.marcadoValido") : t("portal.marcadoNoValido"), estado === "VALIDO");
      setRechazo(null); setMotivo(""); setMotivoErr("");
      load();
    } catch (e) { showToast(e.message || t("common.errorGuardar"), false); }
    finally { setSaving(null); }
  }

  // El rechazo pide motivo: sin él el residente no sabe qué corregir.
  function confirmarRechazo() {
    const texto = motivo.trim();
    if (!texto) { setMotivoErr("Escribí el motivo del rechazo."); return; }
    handleRevisar(rechazo.id, "NO_VALIDO", texto);
  }

  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    try { await deleteAvisoPago(confirm.id); showToast(t("common.eliminado"), true); setConfirm(null); load(); }
    catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  const FILTROS = ["PENDIENTE", "VALIDO", "NO_VALIDO", "TODOS"];
  const pendingCount = avisos.filter(a => a.estado === "PENDIENTE").length;

  const filtered = avisos.filter(a => {
    if (filtro !== "TODOS" && a.estado !== filtro) return false;
    if (filterFilial && String(a.filial) !== filterFilial) return false;
    const fecha = a.created_at?.split("T")[0] || "";
    if (filterDesde && fecha < filterDesde) return false;
    if (filterHasta && fecha > filterHasta) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="portal-admin">
      {toastEl}
      <ConfirmModal open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting} />

      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Portal Admin /</span>
        </Link>
        <span className={styles.active}> {t("portal.avisosPago")}</span>
      </span>

      {/* Header */}
      <div>
        <h2 className="mt-2 p-0 title">
          {t("portal.avisosPago")}
        </h2>
        <p className="mb-3" style={{ color: "var(--text-muted)" }}>
          {t("portal.adminAvisosPagoDesc")}
        </p>
      </div>

      {/* Stats */}
      {!loading && avisos.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
          <StatCard icon="fa-inbox"         label="Total recibidos" value={avisos.length}                                      color="#f97316" />
          <StatCard icon="fa-clock"         label="Pendientes"      value={avisos.filter(a => a.estado === "PENDIENTE").length} color="#f59e0b" />
          <StatCard icon="fa-circle-check"  label="Válidos"         value={avisos.filter(a => a.estado === "VALIDO").length}    color="#10b981" />
          <StatCard icon="fa-circle-xmark"  label="No válidos"      value={avisos.filter(a => a.estado === "NO_VALIDO").length} color="#ef4444" />
        </div>
      )}

      {/* Tab filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {FILTROS.map(f => {
          const lbl = f === "TODOS" ? t("portal.todos") : f === "NO_VALIDO" ? "No válido" : f.charAt(0) + f.slice(1).toLowerCase();
          return (
            <button key={f} onClick={() => { setFiltro(f); setPage(1); }} style={{
              padding: "7px 16px", borderRadius: 20, cursor: "pointer",
              fontWeight: 600, fontSize: 13, border: "none",
              background: filtro === f ? "#f97316" : "var(--hover-bg)",
              color: filtro === f ? "#fff" : "var(--text-secondary)",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: filtro === f ? "0 2px 8px rgba(249,115,22,0.3)" : "none",
              transition: "all 0.15s",
            }}>
              {lbl}
              {f === "PENDIENTE" && pendingCount > 0 && (
                <span style={{ background: "#f87171", color: "#fff", borderRadius: 99, padding: "1px 6px", fontSize: 10, fontWeight: 800 }}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Card with filters + list */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, overflow: "hidden", overflowX: "auto",
      }}>
        <TableToolbar
          loading={loading}
          activeFilterCount={[filterFilial, filterDesde, filterHasta].filter(Boolean).length}
          onClearFilters={() => { setFilterFilial(""); setFilterDesde(""); setFilterHasta(""); setPage(1); }}
          rightActions={
            <span style={{
              fontSize: "0.72rem", color: "var(--text-secondary)",
              background: "var(--hover-bg)", borderRadius: 20, padding: "4px 10px",
              fontWeight: 600, whiteSpace: "nowrap",
            }}>
              {filtered.length} aviso{filtered.length !== 1 ? "s" : ""}
            </span>
          }
          filters={
            <>
              <FilterField label="Filial">
                <FilterSelect value={filterFilial} onChange={(e) => { setFilterFilial(e.target.value); setPage(1); }}>
                  <option value="">Todas las filiales</option>
                  {filiales.map((f) => <option key={f.id} value={f.id}>Filial {f.idFilial}</option>)}
                </FilterSelect>
              </FilterField>
              <FilterField label="Desde">
                <FilterInput type="date" value={filterDesde} onChange={(e) => { setFilterDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label="Hasta">
                <FilterInput type="date" value={filterHasta} onChange={(e) => { setFilterHasta(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        {/* List */}
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-secondary)" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 24, marginBottom: 12, display: "block" }} />
            Cargando avisos…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{
              width: 60, height: 60, borderRadius: "50%",
              background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", color: "#f97316", fontSize: 24,
            }}>
              <i className="fa-solid fa-inbox" />
            </div>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: 15 }}>
              {avisos.length === 0 ? t("portal.sinAvisosPago") : "Sin resultados"}
            </p>
            <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
              {avisos.length === 0 ? "No hay avisos de pago enviados por residentes." : "Probá cambiando los filtros."}
            </p>
          </div>
        ) : (
          <>
            {paged.map((a, i) => (
              <div key={a.id} style={{
                padding: isMobile ? "14px 14px" : "16px 18px",
                borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
                transition: "background 0.12s",
              }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--hover-bg)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {/* Header row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
                        <i className="fa-solid fa-building" style={{ marginRight: 6, opacity: 0.6 }} />
                        {a.filial_nombre}
                      </span>
                      <EstadoBadge estado={a.estado} />
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                      {a.usuario_nombre && (
                        <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                          <i className="fa-solid fa-user" style={{ fontSize: 10, opacity: 0.6 }} />
                          {a.usuario_nombre}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="fa-solid fa-calendar" style={{ fontSize: 10, opacity: 0.6 }} />
                        {a.created_at?.split("T")[0]}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirm({ id: a.id, name: `${a.filial_nombre} - ${a.created_at?.split("T")[0]}` })}
                    title="Eliminar"
                    style={{
                      background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)",
                      borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#f87171", fontSize: 12, flexShrink: 0,
                    }}
                  >
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>

                {/* Detalle */}
                {a.detalle && (
                  <p style={{
                    margin: "0 0 10px", fontSize: 13, color: "var(--text-primary)",
                    background: "var(--hover-bg)", borderRadius: 8,
                    padding: "8px 12px", borderLeft: "3px solid rgba(249,115,22,0.3)",
                  }}>
                    {a.detalle}
                  </p>
                )}

                {/* Acciones */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {a.comprobante_url && (
                    <a href={a.comprobante_url} target="_blank" rel="noreferrer" style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)",
                      color: "#f97316", padding: "6px 12px", borderRadius: 8,
                      textDecoration: "none", fontWeight: 600, fontSize: 12,
                    }}>
                      <i className="fa-solid fa-paperclip" /> Ver comprobante
                    </a>
                  )}
                  {a.estado === "PENDIENTE" && (
                    <>
                      <button onClick={() => { setRechazo(a); setMotivo(""); setMotivoErr(""); }} disabled={saving === a.id} style={{
                        padding: "6px 14px", borderRadius: 8,
                        background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
                        color: "#f87171", fontWeight: 700, fontSize: 12, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        <i className="fa-solid fa-xmark" /> {t("portal.noValido")}
                      </button>
                      <button onClick={() => handleRevisar(a.id, "VALIDO")} disabled={saving === a.id} style={{
                        padding: "6px 14px", borderRadius: 8, border: "none",
                        background: saving === a.id ? "#b45309" : "#f97316",
                        color: "#fff", fontWeight: 700, fontSize: 12,
                        cursor: saving === a.id ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", gap: 5,
                      }}>
                        {saving === a.id
                          ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                          : <><i className="fa-solid fa-check" /> {t("portal.valido")}</>}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            <Pagination page={page} total={filtered.length} onChange={setPage} />
          </>
        )}
      </div>

      {/* ── Motivo del rechazo ── */}
      {rechazo && (
        <div onClick={() => setRechazo(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 16, width: "min(460px, 96vw)", boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
          }}>
            <div style={{ padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)" }}>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>
                Rechazar aviso de pago
              </h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                {rechazo.filial_nombre} · {new Date(rechazo.created_at).toLocaleDateString()}
              </p>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <label style={{
                fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", display: "block",
                marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5,
              }}>
                Motivo <span style={{ color: "#f87171" }}>*</span>
              </label>
              <textarea
                value={motivo}
                onChange={e => { setMotivo(e.target.value); if (motivoErr) setMotivoErr(""); }}
                rows={3}
                placeholder="Ej: el comprobante no se ve, el monto no coincide, la fecha no corresponde…"
                style={{
                  width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
                  border: `1px solid ${motivoErr ? "#f87171" : "var(--border-card)"}`,
                  background: "var(--bg-input, var(--hover-bg))", color: "var(--text-primary)",
                  fontSize: 13, outline: "none", resize: "vertical",
                }}
              />
              <p style={{ margin: "6px 0 0", fontSize: 12, color: motivoErr ? "#f87171" : "var(--text-secondary)" }}>
                {motivoErr || "El residente va a ver este motivo en su notificación."}
              </p>
            </div>
            <div style={{
              padding: "16px 24px 20px", borderTop: "1px solid var(--border-card)",
              display: "flex", gap: 10, justifyContent: "flex-end",
            }}>
              <button onClick={() => setRechazo(null)} style={{
                padding: "9px 20px", borderRadius: 8, border: "1px solid var(--border-card)",
                background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
              }}>
                {t("common.cancelar")}
              </button>
              <button onClick={confirmarRechazo} disabled={saving === rechazo.id} style={{
                padding: "9px 22px", borderRadius: 8, border: "none",
                background: "#ef4444", color: "#fff", fontWeight: 700, fontSize: 14,
                cursor: saving === rechazo.id ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 7,
              }}>
                {saving === rechazo.id
                  ? <><i className="fa-solid fa-circle-notch fa-spin" /> Enviando…</>
                  : <><i className="fa-solid fa-xmark" /> Rechazar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
